import React, { Suspense, lazy, useEffect, useRef } from "react";
import "react-modern-drawer/dist/index.css";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { io } from "socket.io-client";
import "yet-another-react-lightbox/styles.css";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import Footer from "./Components/Footer/Footer.jsx";
import Header from "./Components/Header/Header.jsx";
import Loader from "./Components/Loader";
import Navbar from "./Components/Navbar/Navbar.jsx";
import { socketUrl } from "./Config/apiUrl.js";
// protected route---
import AdminProtectedRoute from "./HelperFunction/AdminProtectedRoute.js";
import BeforeLoginRoute from "./HelperFunction/BeforeLoginRoute.js";
import ProtectedRoute from "./HelperFunction/ProtectedRoute.js";
import "./table.css";
import {
  useCategorySubCategoryQuery,
  useLazyGetNotificationsQuery,
  useLazyGetUserQuery,
} from "./redux/api/apis.js";
import { getAllCategory, getMe } from "./redux/commonSlice.js";
import {
  addNotification,
  allNotification,
  unReadCount,
  updateUnReadCount,
  updateUnReadMessageCount,
} from "./redux/notificationSlice.js";

//  before login routes
const Login = lazy(() => import("./Pages/Login/Login.jsx"));
const Register = lazy(() => import("./Pages/Register/Register.jsx"));
const Home = lazy(() => import("./Pages/Home/Home.jsx"));
const About = lazy(() => import("./Pages/About/About.jsx"));
const Blogs = lazy(() => import("./Pages/Blogs/Blogs.jsx"));
const BlogDetail = lazy(() => import("./Pages/BlogDetail/BlogDetail.jsx"));
const Account = lazy(() => import("./Pages/Account/index.js"));
const Favourite = lazy(() => import("./Pages/Favourite/Favourite.jsx"));
const RedirectDashboard = lazy(() => import("./Pages/RedirectDashboard"));
const NotFound = lazy(() => import("./Pages/NotFound"));
// dashboard-pages---
const DashboardWeb = lazy(() => import("./Pages/Dashboard"));
const Setting = lazy(() => import("./Pages/Setting"));
const ProductProfile = lazy(() =>
  import("./Pages/ProductProfile/ProductProfile.jsx")
);
const Orders = lazy(() => import("./Pages/Orders"));
const OrderDetails = lazy(() => import("./Pages/OrderDetails"));
const BankDetails = lazy(() => import("./Pages/BankDetails"));
const ProductDetail = lazy(() =>
  import("./Pages/ProductDetail/ProductDetail.jsx")
);
const ProductShop = lazy(() => import("./Pages/ProductShop/ProductShop.jsx"));
const SellItem = lazy(() => import("./Pages/SellItems/SellItems.jsx"));
const TrackOrder = lazy(() => import("./Pages/TrackOrder/TrackOrder.jsx"));
const Checkout = lazy(() => import("./Pages/Checkout/Checkout.jsx"));
const Message = lazy(() => import("./Pages/Message"));
const ForgotPassword = lazy(() =>
  import("./Pages/ForgotPassword/ForgotPassword.jsx")
);
const ResetPassword = lazy(() =>
  import("./Pages/ResetPassword/ResetPassword.jsx")
);
const Notification = lazy(() => import("./Pages/Notification"));
// admin pages---
const Dashboard = lazy(() => import("./Pages/AdminPages/Home"));
const Users = lazy(() => import("./Pages/AdminPages/Users"));
const Products = lazy(() => import("./Pages/AdminPages/Products"));
const PayoutRequests = lazy(() => import("./Pages/AdminPages/PayoutRequests"));
const AdminNotification = lazy(() => import("./Pages/AdminPages/Notification"));
const PayoutRequestsDetail = lazy(() =>
  import("./Pages/AdminPages/PayoutRequestsDetail")
);
const RefundRequests = lazy(() => import("./Pages/AdminPages/RefundRequests"));
const RefundRequestsDetail = lazy(() =>
  import("./Pages/AdminPages/RefundRequestsDetail")
);
const AddEditCategories = lazy(() =>
  import("./Pages/AdminPages/AddEditCategories")
);

const PageLayout = ({ children }) => (
  <>
    <Header />
    <Navbar />
    {children}
    <Footer />
  </>
);

const App = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const { isLogin, meData } = useSelector((state) => state?.commonReducer);
  const [trigger, { data, isFetching }] = useLazyGetUserQuery();
  const { data: categoryData, isFetching: isApiCall } =
    useCategorySubCategoryQuery();
  const [
    getNotification,
    { data: notifications, isFetching: isNotificationCall },
  ] = useLazyGetNotificationsQuery();

  useEffect(() => {
    const socket = io(socketUrl);
    socketRef.current = socket;
  }, []);
  useEffect(() => {
    async function getMeFunc() {
      if (isLogin) {
        await trigger();
        if (data) {
          dispatch(getMe(data?.data));
        }
      }
    }
    getMeFunc();
  }, [isLogin, data]);
  if (categoryData) {
    dispatch(getAllCategory(categoryData));
  }
  useEffect(() => {
    async function getNotiFunc() {
      if (isLogin) {
        await getNotification();
        if (notifications) {
          const messageNotification = notifications?.data?.notification?.filter(
            (ele) => ele?.type === "message"
          );
          const otherNotification = notifications?.data?.notification?.filter(
            (ele) => ele?.type !== "message"
          );
          if (messageNotification?.length > 0) {
            dispatch(updateUnReadMessageCount(messageNotification?.length));
          }
          dispatch(allNotification(otherNotification));
          dispatch(unReadCount(otherNotification?.length));
        }
      }
    }
    getNotiFunc();
  }, [isLogin, notifications]);
  // notification--
  useEffect(() => {
    const socket = socketRef.current;
    socket.emit("ADD_USER", meData?._id);
    socket.on("RECEIVE_NOTIFICATION", (message) => {
      if (meData?._id === message?.to?._id) {
        if (message?.type === "message") {
          dispatch(updateUnReadMessageCount("add"));
        } else {
          dispatch(addNotification(message));
          dispatch(updateUnReadCount("add"));
        }
      }
    });
  }, []);
  // clear location state--
  const ClearLocationStateOnSpecificPage = ({ targetPath }) => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      const handleBeforeUnload = () => {
        if (location.pathname === targetPath) {
          navigate(location.pathname, { replace: true, state: null });
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }, [navigate, location, targetPath]);

    return null;
  };

  if (isFetching || isApiCall || isNotificationCall) {
    return <Loader />;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <ClearLocationStateOnSpecificPage targetPath="/messages" />
          <Routes>
            {/* before login routes */}
            <Route
              path="/login"
              element={
                <BeforeLoginRoute
                  file={
                    <PageLayout>
                      <Login />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/register"
              element={
                <BeforeLoginRoute
                  file={
                    <PageLayout>
                      <Register />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/forgot-password"
              element={
                <BeforeLoginRoute
                  file={
                    <PageLayout>
                      <ForgotPassword />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/reset-password/:id"
              element={
                <BeforeLoginRoute
                  file={
                    <PageLayout>
                      <ResetPassword />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/email-verification/:id"
              element={<BeforeLoginRoute file={<RedirectDashboard />} />}
            />
            {/* websites pages */}
            <Route
              path="/"
              element={
                <PageLayout>
                  <Home />
                </PageLayout>
              }
            />
            <Route
              path="/about"
              element={
                <PageLayout>
                  <About />
                </PageLayout>
              }
            />
            <Route
              path="/products/:id"
              element={
                <PageLayout>
                  <ProductShop />
                </PageLayout>
              }
            />
            <Route
              path="/products/:id/details"
              element={
                <PageLayout>
                  <ProductDetail />
                </PageLayout>
              }
            />

            <Route
              path="/blogs"
              element={
                <PageLayout>
                  <Blogs />
                </PageLayout>
              }
            />
            <Route
              path="/blogs/:id"
              element={
                <PageLayout>
                  <BlogDetail />
                </PageLayout>
              }
            />
            <Route
              path="/checkout/:id"
              element={
                <PageLayout>
                  <Checkout />
                </PageLayout>
              }
            />

            {/* After login pages-- */}

            <Route
              path="/favourite"
              element={
                <ProtectedRoute
                  file={
                    <PageLayout>
                      <Favourite />
                    </PageLayout>
                  }
                />
              }
            />

            <Route
              path="/order/track"
              element={
                <ProtectedRoute
                  file={
                    <PageLayout>
                      <TrackOrder />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute
                  file={
                    <PageLayout>
                      <Message />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/notification"
              element={
                <ProtectedRoute
                  file={
                    <PageLayout>
                      <Notification />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="/sell-item"
              element={
                <ProtectedRoute
                  file={
                    <PageLayout>
                      <SellItem />
                    </PageLayout>
                  }
                />
              }
            />
            <Route
              path="*"
              exact
              element={
                <PageLayout>
                  <NotFound />
                </PageLayout>
              }
            />

            {/* dashboard-pages--- */}
            <Route
              path="/products/profile/:id"
              element={<ProtectedRoute file={<ProductProfile />} />}
            />
            <Route
              path="/setting"
              element={<ProtectedRoute file={<Setting />} />}
            />
            <Route
              path="/dashboard"
              element={<ProtectedRoute file={<DashboardWeb />} />}
            />
            <Route
              path="/orders"
              element={<ProtectedRoute file={<Orders />} />}
            />
            <Route
              path="/orders/:id"
              element={<ProtectedRoute file={<OrderDetails />} />}
            />
            <Route
              path="/account"
              element={<ProtectedRoute file={<Account />} />}
            />
            <Route
              path="/bank-details"
              element={<ProtectedRoute file={<BankDetails />} />}
            />

            {/* admin pages----------- */}
            <Route
              path="/admin/dashboard"
              element={<AdminProtectedRoute file={<Dashboard />} />}
            />
            <Route
              path="/admin/add-edit-categories"
              element={<AdminProtectedRoute file={<AddEditCategories />} />}
            />
            <Route
              path="/admin/users"
              element={<AdminProtectedRoute file={<Users />} />}
            />
            <Route
              path="/admin/products"
              element={<AdminProtectedRoute file={<Products />} />}
            />
            <Route
              path="/admin/payout-requests"
              element={<AdminProtectedRoute file={<PayoutRequests />} />}
            />
            <Route
              path="/admin/payout-requests/:id"
              element={<AdminProtectedRoute file={<PayoutRequestsDetail />} />}
            />
            <Route
              path="/admin/refund-requests"
              element={<AdminProtectedRoute file={<RefundRequests />} />}
            />
            <Route
              path="/admin/refund-requests/:id"
              element={<AdminProtectedRoute file={<RefundRequestsDetail />} />}
            />
            <Route
              path="/admin/notification"
              element={<AdminProtectedRoute file={<AdminNotification />} />}
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
};
export default App;
